import type { StrategyFormState, StrategyAction, ScienceRate, ScienceBranch } from '../../types/strategy';
import { COLORS, INPUT_STYLE } from '../../constants/theme';
import { NumericInput } from './NumericInput';

interface ScienceTabProps {
  state: StrategyFormState;
  dispatch: React.Dispatch<StrategyAction>;
  totalLand: number;
  existingBooks: Record<string, number>;
  /** SF-172: `'touch'` renders 44px-min-height/16px-font `NumericInput`s (mobile); default
   *  preserves today's exact desktop styling. */
  inputSize?: 'default' | 'touch';
  /** SF-172: forwarded to every `NumericInput`'s own `resetKey` — clears any in-progress typed
   *  draft on an external bulk mutation (e.g. loading a template mid-edit). */
  resetKey?: number;
}

const SCIENCE_RATES: { value: ScienceRate; label: string; bpa: number; gc: number }[] = [
  { value: 'NO_RESEARCH', label: 'None', bpa: 0, gc: 0 },
  { value: 'MINIMAL', label: 'Minimal', bpa: 0.3, gc: 6 },
  { value: 'LIMITED', label: 'Limited', bpa: 0.4, gc: 7 },
  { value: 'SUSTAINED', label: 'Sustained', bpa: 0.5, gc: 9 },
  { value: 'ACTIVE', label: 'Active', bpa: 0.7, gc: 13 },
  { value: 'FOCUSED', label: 'Focused', bpa: 0.9, gc: 17 },
  { value: 'ACCELERATED', label: 'Accelerated', bpa: 1.2, gc: 22 },
  { value: 'INTENSIVE', label: 'Intensive', bpa: 1.5, gc: 27 },
  { value: 'RUSHED', label: 'Rushed', bpa: 2.0, gc: 36 },
  { value: 'EXTREME', label: 'Extreme', bpa: 3.0, gc: 50 },
];

const BRANCHES: { key: ScienceBranch; label: string; desc: string }[] = [
  { key: 'alchemy', label: 'Alchemy', desc: 'Gold income' },
  { key: 'tools', label: 'Tools', desc: 'Building efficiency' },
  { key: 'housing', label: 'Housing', desc: 'Population capacity' },
  { key: 'food', label: 'Food', desc: 'Food production' },
  { key: 'military', label: 'Military', desc: 'Military effectiveness' },
  { key: 'thievery', label: 'Thievery', desc: 'Thief effectiveness' },
  { key: 'channeling', label: 'Channeling', desc: 'Spell effectiveness, rune production' },
];

const selectStyle = {
  ...INPUT_STYLE,
  border: `1px solid ${COLORS.inputBorder}`,
} as const;

export function ScienceTab({ state, dispatch, inputSize, resetKey }: ScienceTabProps) {
  const allocSum = Object.values(state.scienceAlloc).reduce((s, v) => s + v, 0);

  return (
    <div className="space-y-6">
      {/* Research Rate */}
      <div>
        <div className="text-sm mb-1" style={{ color: COLORS.text.secondary }}>Research Rate</div>
        <select
          value={state.scienceRate}
          onChange={(e) => dispatch({ type: 'SET_SCIENCE_RATE', value: e.target.value as ScienceRate })}
          className="w-full max-w-xs rounded text-sm py-1.5 px-2"
          style={selectStyle}
        >
          {SCIENCE_RATES.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}{r.bpa > 0 ? ` (${r.bpa} BPA, ${r.gc}gc/book)` : ''}
            </option>
          ))}
        </select>
      </div>

      {/* Branch Allocation */}
      <div>
        <div className="text-sm mb-2" style={{ color: COLORS.text.secondary }}>Science Allocation</div>

        {/* Allocation bar */}
        <div className="mb-3">
          <div className="flex justify-between text-xs mb-1" style={{ color: COLORS.text.tertiary }}>
            <span>Allocation</span>
            <span className={Math.abs(allocSum - 100) > 0.5 ? 'text-yellow-400' : ''} style={Math.abs(allocSum - 100) > 0.5 ? undefined : { color: COLORS.text.secondary }}>
              {allocSum.toFixed(0)}% / 100%
            </span>
          </div>
          <div className="w-full rounded-full h-2 overflow-hidden" style={{ backgroundColor: COLORS.border }}>
            <div
              className={`h-full rounded-full transition-all ${Math.abs(allocSum - 100) > 0.5 ? 'bg-yellow-500' : 'bg-blue-500'}`}
              style={{ width: `${Math.min(allocSum, 100)}%` }}
            />
          </div>
          {Math.abs(allocSum - 100) > 0.5 && (
            <div className="text-xs text-yellow-400 mt-1">
              Must sum to 100% (currently {allocSum.toFixed(0)}%)
            </div>
          )}
        </div>

        <div className="grid grid-cols-[100px_1fr_60px] gap-2 text-xs px-1 mb-1" style={{ color: COLORS.text.tertiary }}>
          <span>Branch</span>
          <span>Increases</span>
          <span className="text-right">%</span>
        </div>
        {BRANCHES.map(({ key, label, desc }) => {
          const pct = state.scienceAlloc[key];
          return (
            <div key={key} className="grid grid-cols-[100px_1fr_60px] gap-2 items-center px-1 py-1">
              <span className="text-sm" style={{ color: COLORS.text.secondary }}>{label}</span>
              <span className="text-xs" style={{ color: COLORS.text.tertiary }}>{desc}</span>
              <NumericInput
                value={pct}
                max={100}
                onChange={(v) => dispatch({ type: 'SET_SCIENCE_ALLOC', branch: key, value: v })}
                className="w-14 ml-auto"
                ariaLabel={`${label} allocation percent`}
                size={inputSize}
                resetKey={resetKey}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
